import React, { useState, useCallback } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { toast } from 'sonner';
import { submitContactForm } from '../../mock';

const ContactForm = ({ t, language, configurationSummary = '' }) => {
  const [formData, setFormData] = useState({
    name: '',
    company: '',
    email: '',
    phone: '',
    additionalMessage: '' // User's own notes/message
  });
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleInputChange = useCallback((e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  }, []);

  // Combine config summary + user message for submission
  const getFullMessage = useCallback(() => {
    const parts = [];
    if (configurationSummary) {
      parts.push(configurationSummary);
    }
    if (formData.additionalMessage.trim()) {
      parts.push(formData.additionalMessage.trim());
    }
    return parts.join('\n\n---\n\n');
  }, [configurationSummary, formData.additionalMessage]);

  const handleFormSubmit = useCallback(async (e) => {
    e.preventDefault();
    
    if (isSubmitting) return;
    
    // Validate required fields with localized, field-specific messages
    if (!formData.email.trim()) {
      toast.error(language === 'cs' ? 'Vyplňte prosím e-mail' : 'Please fill in your email');
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(formData.email.trim())) {
      toast.error(language === 'cs' ? 'Zadejte prosím platný e-mail' : 'Please enter a valid email address');
      return;
    }
    if (!formData.phone.trim()) {
      toast.error(language === 'cs' ? 'Vyplňte prosím telefon' : 'Please fill in your phone number');
      return;
    }
    if ((formData.phone.match(/\d/g) || []).length < 6) {
      toast.error(language === 'cs' ? 'Zadejte prosím platné telefonní číslo' : 'Please enter a valid phone number');
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      const fullMessage = getFullMessage();
      const result = await submitContactForm(
        { 
          ...formData, 
          message: fullMessage,
          language 
        }, 
        null
      );
      
      if (result.success) {
        toast.success(t.formSuccessMessage);
        setFormSubmitted(true);
        setFormData({ name: '', company: '', email: '', phone: '', additionalMessage: '' });
        setTimeout(() => setFormSubmitted(false), 5000);
      }
    } catch (error) {
      toast.error(t.formErrorMessage);
    } finally {
      setIsSubmitting(false);
    }
  }, [formData, language, t, isSubmitting, getFullMessage]);

  // Get config summary label based on language
  const configLabel = language === 'cs' ? 'Vaše konfigurace:' : 'Your configuration:';

  const additionalNotesLabel = language === 'cs' ? 'Doplňující poznámky (nepovinné):' : 'Additional notes (optional):';

  return (
    <section id="contact" className="contact-section" aria-labelledby="contact-title">
      <h2 id="contact-title" className="section-title">{t.contactTitle}</h2>
      <p className="section-subtitle">{t.contactSubtitle}</p>
      
      {formSubmitted ? (
        <div className="success-message" role="alert" aria-live="polite">{t.formSuccess}</div>
      ) : (
        <form className="contact-form" onSubmit={handleFormSubmit} noValidate>
          <div className="form-row">
            <Input
              name="name"
              placeholder={t.formName}
              value={formData.name}
              onChange={handleInputChange}
              aria-label={t.formName}
              data-testid="contact-name-input"
            />
            <Input
              name="company"
              placeholder={t.formCompany}
              value={formData.company}
              onChange={handleInputChange}
              aria-label={t.formCompany}
              data-testid="contact-company-input"
            />
          </div>
          <div className="form-row">
            <Input
              name="email"
              type="email"
              placeholder={`${t.formEmail} *`}
              value={formData.email}
              onChange={handleInputChange}
              required
              aria-label={t.formEmail}
              aria-required="true"
              data-testid="contact-email-input"
            />
            <Input
              name="phone"
              type="tel"
              placeholder={`${t.formPhone} *`}
              value={formData.phone}
              onChange={handleInputChange}
              required
              aria-label={t.formPhone}
              aria-required="true"
              data-testid="contact-phone-input"
            />
          </div>
          
          {/* Auto-generated Configuration Summary Block */}
          {configurationSummary && (
            <div className="config-summary-block" data-testid="config-summary-block">
              <div className="config-summary-label">{configLabel}</div>
              <div className="config-summary-content" aria-live="polite">
                {configurationSummary.split('\n').map((line, idx) => (
                  <div key={`config-line-${idx}`} className="config-line">{line}</div>
                ))}
              </div>
            </div>
          )}
          
          {/* User's Additional Message */}
          <div className="additional-message-wrapper">
            <label className="additional-message-label" htmlFor="additionalMessage">
              {additionalNotesLabel}
            </label>
            <Textarea
              id="additionalMessage"
              name="additionalMessage"
              placeholder={t.formMessage}
              value={formData.additionalMessage}
              onChange={handleInputChange}
              rows={4}
              aria-label={additionalNotesLabel}
              data-testid="contact-message-input"
            />
          </div>
          
          <Button 
            type="submit" 
            className="submit-button"
            disabled={isSubmitting}
            data-testid="contact-submit-btn"
            aria-busy={isSubmitting}
          >
            {isSubmitting ? (
              language === 'cs' ? 'Odesílání...' : 'Sending...'
            ) : (
              t.formSubmit
            )}
          </Button>
        </form>
      )}
    </section>
  );
};

export default React.memo(ContactForm);
